import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { MiddlewareAPI, Middleware } from '@reduxjs/toolkit';
import { isRejectedWithValue } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/browser';
import { clone, stripNull } from 'src/utils/object';
import baseUrl from 'src/utils/baseUrl';
import objFromArray from 'src/utils/objFromArray';
import { constructPayload } from 'src/services/adminService';
import { localizeHistoryTime } from 'src/views/tasks/api';
import socket from 'src/utils/socket';
import { DashboardSocketEvent } from 'types';
import type {
  Checklist,
  CaseBannerType,
  SerologyAllApi,
  SerologyReportApi,
  TissueRecoveryResp,
  RecoverySuppliesResp,
  CatalogResp,
  LookupType,
  DashboardCaseType,
  DashboardSocketEventType,
  TissueRecoveryForm,
  UsersApi,
  UsersByRoleApi,
  AuthorizedUsagesApi,
  DonationDiscussionApi,
  DispatchDashboardApi,
  HemodilutionInformationApi,
  HemodilutionInformationResponseApi,
  HemodilutionUpsertRequestApi,
  HemodilutionSerologyApi,
  HemodilutionSerologiesResponseApi,
  TransmitSelectableOrganization,
  TransmitOrganizationContact,
  LogTransmitDownloadRequest,
  UserEmailOption,
} from 'types';
import type {
  NoteTemplateSchema,
  TransmitTemplateBaseSchema,
  TransmitTemplateSchema,
} from 'types/db';
import type {
  GetOrganizationProfileResponseDTO,
  OrganizationOperationResponseDTO,
  OrganizationContactsResponseDTO,
  OrganizationListResponseDTO,
  OrganizationListItemDTO,
  UpdateOrganizationDTO,
  CreateOrganizationDTO,
} from 'types/api/dto';
import { setCurrentFeedback } from 'src/actions/feedbackActions';

const isTest = import.meta.env.REACT_APP_ENVIRONMENT === 'testing';
const shortLife = isTest ? 0 : 5 * 60;
const longLife = isTest ? 0 : 30 * 60;

type TransmitTemplatePayload = Pick<
  TransmitTemplateBaseSchema,
  'body' | 'subjectLine'
> &
  Partial<Pick<TransmitTemplateBaseSchema, 'active' | 'name'>>;

type UpdateTransmitTemplateRequest = TransmitTemplatePayload & {
  templateId: string | number;
};

type TransmitTemplatesResponse = {
  templates: TransmitTemplateSchema[];
};

type TransmitTemplateResponse = {
  template: TransmitTemplateSchema;
};

// Define a service using a base URL and expected endpoints
export const serverState = createApi({
  reducerPath: 'serverState',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      headers.set('Timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
    },
    credentials: 'include',
  }),
  tagTypes: [
    'AuthForms',
    'Case',
    'CaseBanner',
    'CaseContacts',
    'CaseChecklist',
    'CaseLock',
    'CaseSummary',
    'CaseWorkSheet',
    'CaseNotes',
    'CaseTasks',
    'Dispatch',
    'Donor',
    'DonorScreening',
    'DonorShipping',
    'DonationDiscussions',
    'DonationScreenings',
    'Groups',
    'Hemodilution',
    'Lookups',
    'NoteTemplates',
    'Organization',
    'Organizations',
    'Outcomes',
    'Partners',
    'ReferralWorksheet',
    'Recovery',
    'Referrals',
    'Serology',
    'TaskAuxData',
    'TransmitTemplates',
    'User',
    'Users',
  ],
  endpoints: (build) => ({
    logTransmitDownload: build.mutation<
      { message: string },
      LogTransmitDownloadRequest
    >({
      query: (request) => {
        const formData = new FormData();

        if ('transmitLogId' in request) {
          formData.append('transmitLogId', String(request.transmitLogId));
        } else {
          formData.append('buffer', request.buffer, 'transmit.pdf');
          formData.append('details', JSON.stringify(request.details));
          formData.append(
            'messageDetails',
            JSON.stringify(request.messageDetails),
          );
          formData.append('sender', request.sender);
          formData.append('senderEmail', request.senderEmail);
        }

        return {
          url: `/public/transmit/log-download/${request.caseId}`,
          method: 'POST',
          body: formData,
        };
      },
    }),
    getTransmitOrganizations: build.query<
      TransmitSelectableOrganization[],
      string
    >({
      query: (caseId) =>
        `/public/v2/organization/case/${caseId}/availableOrganizations`,
      transformResponse: (response: {
        organizations: TransmitSelectableOrganization[];
      }) => response?.organizations || [],
      keepUnusedDataFor: shortLife,
    }),
    getTransmitOrganizationContacts: build.query<
      TransmitOrganizationContact[],
      string | number
    >({
      query: (organizationId) =>
        `/public/v2/organization/${organizationId}/transmit-contacts`,
      transformResponse: (response: {
        organizationContacts: TransmitOrganizationContact[];
      }) => response?.organizationContacts || [],
      keepUnusedDataFor: shortLife,
    }),
    getTransmitLogs: build.query({
      query: (caseId) => `/public/transmitLog/${caseId}`,
      transformResponse: (response: any) => response?.transmitLogs || [],
      keepUnusedDataFor: shortLife,
    }),
    getNoteTemplates: build.query<NoteTemplateSchema[], void>({
      query: () => '/public/noteTemplates',
      transformResponse: (response: { noteTemplates: NoteTemplateSchema[] }) =>
        response?.noteTemplates || [],
      providesTags: ['NoteTemplates'],
      keepUnusedDataFor: shortLife,
    }),
    getNoteTemplate: build.query<NoteTemplateSchema, string | number>({
      query: (templateId) => `/public/noteTemplates/${templateId}`,
      transformResponse: (response: { noteTemplate: NoteTemplateSchema }) =>
        response?.noteTemplate,
      providesTags: (_result, _error, templateId) => [
        'NoteTemplates',
        { type: 'NoteTemplates', id: templateId },
      ],
      keepUnusedDataFor: shortLife,
    }),
    updateNoteTemplate: build.mutation({
      query: ({ templateId, ...body }) => ({
        url: `/public/noteTemplates/${templateId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { templateId }) => [
        'NoteTemplates',
        { type: 'NoteTemplates', id: templateId },
      ],
    }),
    createNoteTemplate: build.mutation({
      query: (body) => ({
        url: '/public/noteTemplates',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['NoteTemplates'],
    }),
    getTransmitTemplates: build.query<TransmitTemplateSchema[], void>({
      query: () => '/public/templates/transmit',
      transformResponse: (response: TransmitTemplatesResponse) =>
        response?.templates || [],
      providesTags: ['TransmitTemplates'],
      keepUnusedDataFor: shortLife,
    }),
    createTransmitTemplate: build.mutation<
      TransmitTemplateResponse,
      TransmitTemplatePayload
    >({
      query: (body) => ({
        url: '/public/templates/transmit',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TransmitTemplates'],
    }),
    updateTransmitTemplate: build.mutation<
      TransmitTemplateResponse,
      UpdateTransmitTemplateRequest
    >({
      query: ({ templateId, ...body }) => ({
        url: `/public/templates/transmit/${templateId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['TransmitTemplates'],
    }),
    getCaseSummary: build.query({
      query: (caseId) => `/public/cases/${caseId}/summaries`,
      transformResponse: (response: any) => response?.caseItem,
      providesTags: ['CaseSummary'],
      keepUnusedDataFor: shortLife,
    }),
    updateCaseSummary: build.mutation({
      query: ({ caseId, ...body }) => ({
        url: `/public/cases/${caseId}/summaries`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['CaseSummary', 'Case'],
    }),
    getCaseBannerInfo: build.query<CaseBannerType, string>({
      query: (caseId) => `/public/cases/${caseId}`,
      keepUnusedDataFor: shortLife,
      providesTags: (_result, _err, caseId) => [
        { type: 'CaseBanner', id: caseId },
      ],
    }),
    getCaseContacts: build.query({
      query: (caseId) => `/public/caseContact/${caseId}`,
      transformResponse: (response: any) => response?.contact,
      providesTags: ['CaseContacts'],
      keepUnusedDataFor: shortLife,
    }),
    addCaseContacts: build.mutation({
      query: ({ caseId, ...body }) => ({
        url: `/public/caseContact/${caseId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CaseContacts'],
    }),
    updateCaseContacts: build.mutation({
      query: ({ caseId, ...body }) => ({
        url: `/public/caseContact/${caseId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['CaseContacts'],
    }),
    getDonationDiscussion: build.query<DonationDiscussionApi, string>({
      query: (caseId) => `/public/cases/${caseId}/donationDiscussions`,
      transformResponse: (response: { record: DonationDiscussionApi }) =>
        response?.record,
      providesTags: ['DonationDiscussions'],
      keepUnusedDataFor: shortLife,
    }),
    updateDonationDiscussion: build.mutation<
      DonationDiscussionApi,
      DonationDiscussionApi
    >({
      query: (body) => ({
        url: `/public/cases/${body.caseId}/donationDiscussions`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: any) => response?.record,
      invalidatesTags: ['DonationDiscussions'],
    }),
    getAuthorizedUsage: build.query<AuthorizedUsagesApi, string>({
      query: (caseId) => `/public/cases/${caseId}/authorizedUsage`,
      transformResponse: (response: { record: AuthorizedUsagesApi }) =>
        response?.record,
      keepUnusedDataFor: shortLife,
    }),
    updateAuthorizedUsage: build.mutation<
      AuthorizedUsagesApi,
      AuthorizedUsagesApi
    >({
      query: (body) => ({
        url: `/public/cases/${body.caseId}/authorizedUsage`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: any) => response?.record,
      invalidatesTags: (_result, _err, body) => [
        { type: 'CaseBanner', id: String(body.caseId) },
        'Case',
      ],
    }),
    getHemodilutionInformation: build.query<HemodilutionInformationApi, string>(
      {
        query: (caseId) => `/public/medicalReview/${caseId}/hemodilution`,
        transformResponse: (response: HemodilutionInformationResponseApi) =>
          response?.record,
        providesTags: ['Hemodilution'],
        keepUnusedDataFor: shortLife,
      },
    ),
    updateHemodilutionInformation: build.mutation<
      HemodilutionInformationApi,
      HemodilutionUpsertRequestApi
    >({
      query: ({ caseId, ...body }) => ({
        url: `/public/medicalReview/${caseId}/hemodilution`,
        method: 'POST',
        body: stripNull({
          ...body,
          serverState: null,
        }),
      }),
      invalidatesTags: ['Hemodilution'],
    }),
    getHemodilutionSerologies: build.query<HemodilutionSerologyApi[], string>({
      query: (caseId) =>
        `/public/medicalReview/${caseId}/hemodilutionSerologies`,
      transformResponse: (response: HemodilutionSerologiesResponseApi) =>
        response?.serologies,
      keepUnusedDataFor: shortLife,
    }),
    getDonorScreening: build.query({
      query: (caseId) => `/public/cases/${caseId}/screenings`,
      transformResponse: (response: any) => {
        if (response.record.previouslyOnVent) {
          const [time, units] = (response.record.durationVent || ' ').split(
            ' ',
          );
          response.record.durationVentTime = time;
          response.record.durationVentUnits = units;
        }

        return response?.record;
      },
      providesTags: ['DonorScreening'],
      keepUnusedDataFor: shortLife,
    }),
    addDonorScreening: build.mutation({
      query: ({ caseId, ...body }) => {
        if (body.previouslyOnVent) {
          body.durationVent = `${body.durationVentTime} ${body.durationVentUnits}`;
        } else {
          body.durationVent = null;
        }
        delete body.durationVentTime;
        delete body.durationVentUnits;

        return {
          url: `/public/cases/${caseId}/screenings`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['DonorScreening'],
    }),
    updateDonorScreening: build.mutation({
      query: ({ caseId, ...body }) => {
        if (body.previouslyOnVent) {
          body.durationVent = `${body.durationVentTime} ${body.durationVentUnits}`;
        } else {
          body.durationVent = null;
        }
        delete body.durationVentTime;
        delete body.durationVentUnits;

        return {
          url: `/public/cases/${caseId}/screenings`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['DonorScreening'],
    }),
    getReferralWorksheet: build.query({
      query: (caseId) => `/public/cases/${caseId}/worksheet`,
      transformResponse: (response: any) => response?.caseItem,
      providesTags: ['ReferralWorksheet'],
      keepUnusedDataFor: shortLife,
    }),
    addReferralWorksheet: build.mutation({
      query: ({ caseId, ...body }) => ({
        url: `/public/cases`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ReferralWorksheet', 'Referrals'],
    }),
    updateReferralWorksheet: build.mutation({
      query: ({ caseId, ...body }) => ({
        url: `/public/cases/${caseId}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: any) => response?.caseItem,
      invalidatesTags: (_result, _err, body) => [
        { type: 'CaseBanner', id: String(body.caseId) },
        'ReferralWorksheet',
        'Referrals',
        'CaseSummary',
        'Case',
      ],
    }),
    getCaseNotes: build.query({
      query: (caseId) => `/public/caseNotes/${caseId}`,
      transformResponse: (response: any) => {
        const byId = objFromArray(response?.notes);
        const allIds = Object.keys(byId);
        return { allIds, byId, notes: response?.notes };
      },
      providesTags: ['CaseNotes'],
      keepUnusedDataFor: shortLife,
    }),
    addCaseNote: build.mutation({
      query: ({ ...body }) => ({
        url: `/public/notes`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CaseNotes'],
    }),
    getAllTasksOnCase: build.query({
      query: (caseId) => `/public/tasks/getAllOnCase?caseId=${caseId}`,
      transformResponse: (response: any) => {
        return localizeHistoryTime(response?.tasks);
      },
      providesTags: ['CaseTasks'],
      keepUnusedDataFor: shortLife,
    }),
    getAllTasks: build.query({
      query: () => `/public/tasks/getAll`,
      transformResponse: (response: any) => {
        return response?.tasks;
      },
      providesTags: ['CaseTasks'],
      keepUnusedDataFor: longLife,
    }),
    addCaseTask: build.mutation({
      query: ({ ...body }) => ({
        url: `/public/tasks/create`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CaseTasks'],
    }),
    updateCaseTask: build.mutation({
      query: ({ taskId, ...body }) => ({
        url: `/public/tasks/update/${taskId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['CaseTasks'],
    }),
    getDispatch: build.query<DispatchDashboardApi[], string>({
      query: (caseId) => `/public/cases/${caseId}/dispatch`,
      providesTags: ['Dispatch'],
      keepUnusedDataFor: shortLife,
    }),
    upsertDispatch: build.mutation({
      query: ({ caseId, ...body }) => {
        return {
          url: `/public/cases/${caseId}/dispatch`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Dispatch'],
    }),
    deleteDispatch: build.mutation({
      query: ({ caseId, id }) => ({
        url: `/public/cases/${caseId}/dispatch/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Dispatch'],
    }),
    getChecklist: build.query<Checklist[], string>({
      query: (caseId) => `/public/caseChecklist/${caseId}`,
      providesTags: ['CaseChecklist'],
      keepUnusedDataFor: longLife,
    }),
    addChecklist: build.mutation({
      query: ({ caseId, data }) => ({
        url: `/public/caseChecklist/${caseId}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['CaseChecklist'],
    }),
    updateChecklist: build.mutation({
      query: ({ caseId, data }) => ({
        url: `/public/caseChecklist/${caseId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['CaseChecklist'],
    }),
    getTaskAuxillaryData: build.query({
      query: () => `/public/tasks/getAuxData`,
      providesTags: ['TaskAuxData'],
      keepUnusedDataFor: longLife,
    }),
    getTaskAllCaseIds: build.query({
      query: () => `/public/tasks/getAllCaseIds`,
      providesTags: ['Case'],
      keepUnusedDataFor: longLife,
    }),
    getLookups: build.query({
      query: () => `/public/lookups`,
      transformResponse: (response: any) => {
        return {
          ...response.record,
          initialState: clone(response.record),
        };
      },
      keepUnusedDataFor: longLife,
      providesTags: ['Lookups'],
    }),
    updateLookups: build.mutation({
      query: ({ ...body }) => ({
        url: `/public/lookups`,
        method: 'PUT',
        body: { payload: constructPayload(body) },
      }),
      invalidatesTags: ['Lookups', 'Outcomes'],
    }),
    getOutcomes: build.query({
      query: (flag) => `/public/outcomes/${flag}`,
      providesTags: ['Outcomes'],
      keepUnusedDataFor: longLife,
    }),
    getOutcomesAll: build.query({
      query: () => `/public/outcomes/all`,
      providesTags: ['Outcomes'],
      keepUnusedDataFor: longLife,
    }),
    getOutcomesDetails: build.query({
      query: (flag) => {
        const addUrl = [];
        if (flag.type) {
          addUrl.push(`type=${flag.type}`);
        }
        if (flag.outcome) {
          addUrl.push(`outcome=${flag.outcome}`);
        }
        return {
          url: `/public/outcomes/details/?${addUrl.join('&')}`,
          method: 'get',
        };
      },
      providesTags: ['Outcomes'],
      keepUnusedDataFor: longLife,
    }),
    getOrganizationsV2: build.query({
      query: ({ roleTypes, orgTypes }) => {
        const params = new URLSearchParams();

        if (roleTypes) params.append('roleTypes', roleTypes);
        if (orgTypes) params.append('orgTypes', orgTypes);

        const queryString = params.toString();
        const url = `/public/v2/organizations${queryString ? `?${queryString}` : ''}`;
        return url;
      },
      transformResponse: (response: any) => {
        return response?.organizations;
      },
      providesTags: ['Organizations'],
      keepUnusedDataFor: longLife,
    }),
    getExternalTables: build.query({
      query: () => `/public/v2/organization/profile/lookups`,
      transformResponse: (response: any) => {
        return response?.record;
      },
    }),
    getAllOrganizations: build.query<OrganizationListItemDTO[], void>({
      query: () => `/public/v2/organizations`,
      transformResponse: (response: OrganizationListResponseDTO) =>
        response?.organizations,
      providesTags: ['Organizations'],
    }),
    getAllOrgsContacts: build.query<
      OrganizationContactsResponseDTO['organizationContacts'],
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: `public/v2/organization/${organizationId}/contacts?active=true`,
      }),
      transformResponse: (response: any) => {
        return response?.organizationContacts;
      },
    }),
    createOrganization: build.mutation<
      OrganizationOperationResponseDTO,
      CreateOrganizationDTO
    >({
      query: (body) => ({
        url: '/public/v2/organization',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Organization', 'Organizations'],
    }),
    updateOrganization: build.mutation<
      OrganizationOperationResponseDTO,
      UpdateOrganizationDTO
    >({
      query: (body) => {
        return {
          url: `/public/v2/organization/${body.id}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['Organization', 'Organizations'],
    }),
    getOrganization: build.query<
      GetOrganizationProfileResponseDTO['record'],
      string
    >({
      query: (orgId) => `/public/v2/organization/${orgId}/profile`,
      transformResponse: (response: GetOrganizationProfileResponseDTO) => {
        return response?.record;
      },
      providesTags: ['Organization'],
    }),
    getCaseAssignmentUsers: build.query({
      query: () => '/public/usersInCaseAssignments',
      providesTags: ['Users'],
      keepUnusedDataFor: longLife,
    }),
    getCaseAssignmentGroups: build.query({
      query: () => '/public/groupsInCaseAssignments',
      providesTags: ['Groups'],
      keepUnusedDataFor: longLife,
    }),
    getUsers: build.query<UsersApi[], void>({
      query: () => '/public/users',
      transformResponse: (response: { users: UsersApi[] }) => response?.users,
      providesTags: ['Users'],
      keepUnusedDataFor: longLife,
    }),
    getUserEmails: build.query<
      UserEmailOption[],
      { includeUnitEmails?: boolean } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.includeUnitEmails) {
          queryParams.append('includeUnitEmails', 'true');
        }

        const queryString = queryParams.toString();
        return `/public/users/emails${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (response: { emails: UserEmailOption[] }) =>
        response?.emails || [],
      providesTags: ['Users'],
      keepUnusedDataFor: longLife,
    }),
    getUsersByRole: build.query<UsersByRoleApi[], string>({
      query: (roles) => `/public/userRoles?roles=${roles}`,
      transformResponse: (response: { usersByRole: UsersByRoleApi[] }) =>
        response?.usersByRole,
      providesTags: ['Users'],
      keepUnusedDataFor: longLife,
    }),
    getUsersByMultipleRoles: build.query<UsersByRoleApi[], string[]>({
      query: (roles) => {
        const rolesParam = roles.join(',');
        return `/public/userRoles?roles=${rolesParam}`;
      },
      transformResponse: (response: { usersByRole: UsersByRoleApi[] }) =>
        response?.usersByRole,
      providesTags: ['Users'],
      keepUnusedDataFor: longLife,
    }),
    getDashboardCases: build.query<DashboardCaseType[], undefined | void>({
      query: () => `/public/dashboard`,
      providesTags: ['Referrals'],
      keepUnusedDataFor: longLife,
      async onCacheEntryAdded(
        _arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch },
      ) {
        try {
          await cacheDataLoaded;
        } catch {
          return;
        }
        const handleDashboardEvent = (message: unknown) => {
          const parsedEvent = DashboardSocketEvent.safeParse(message);
          console.log('parsed event', parsedEvent);
          if (!parsedEvent.success) return;

          const event: DashboardSocketEventType = parsedEvent.data;
          let needsRefetch = false;
          updateCachedData((draft) => {
            const row = draft.find((c) => c.caseId === event.caseId);
            if ('changes' in event) {
              if (!row) {
                needsRefetch = true;
                return;
              }

              Object.assign(row, event.changes);
              if (row.viewTags.length === 0) {
                draft.splice(draft.indexOf(row), 1);
              }
              return;
            }

            if (event.type === 'viewTag.add') {
              if (!row) {
                // The dataset only holds tagged cases, so a newly tagged case
                // is absent from the cache; the HTTP endpoint stays authoritative.
                needsRefetch = true;
                return;
              }
              if (!row.viewTags.includes(event.viewTag)) {
                row.viewTags.push(event.viewTag);
              }
              return;
            }
            if (!row) return;
            row.viewTags = row.viewTags.filter((tag) => tag !== event.viewTag);
            if (row.viewTags.length === 0) {
              draft.splice(draft.indexOf(row), 1);
            }
          });
          if (needsRefetch) {
            dispatch(serverState.util.invalidateTags(['Referrals']));
          }
        };
        socket.on('dashboard', handleDashboardEvent);
        await cacheEntryRemoved;
        socket.off('dashboard', handleDashboardEvent);
      },
    }),
    getLookup: build.query<LookupType[], any>({
      query: ({ modelName, attributes, order, where }) => {
        let url = `${modelName}?`;
        if (attributes && typeof attributes !== 'undefined') {
          url += `attributes=${attributes}&`;
        }
        if (order && typeof order !== 'undefined') {
          url += `order=${order}&`;
        }

        if (where && typeof where !== 'undefined') {
          url += `where=${where}&`;
        }

        return {
          url: `/public/lookups/${url}`,
          method: 'get',
        };
      },
      transformResponse: (response: any) => response?.lookups,
      providesTags: ['Lookups'],
      keepUnusedDataFor: longLife,
    }),
    getOrganizationProtocol: build.query({
      query: (orgId) => `/public/v2/organization/${orgId}/attachment`,
      transformResponse: (response: any) => response?.attachment,
    }),
    uploadOrganizationProtocol: build.mutation({
      query: ({ orgId, file }) => {
        const formData = new FormData();
        formData.append('upload', file);

        return {
          url: `/public/v2/organization/${orgId}/attachment`,
          method: 'POST',
          body: formData,
        };
      },
    }),
    softDeleteOrganizationProtocol: build.mutation({
      query: (orgId) => ({
        url: `/public/v2/organization/${orgId}/attachment`,
        method: 'DELETE',
      }),
    }),
    getPartnersV2: build.query({
      query: () => `/public/v2/partners`,
      transformResponse: (response: any) => response?.partners,
      providesTags: ['Partners'],
      keepUnusedDataFor: longLife,
    }),
    getOrganizationPartnersV2: build.query({
      query: (orgId: number | string) =>
        `/public/v2/organization/${orgId}/partners`,
      transformResponse: (response: any) => response?.organizationPartners,
      providesTags: ['Partners'],
      keepUnusedDataFor: longLife,
    }),
    getOrganizationRecoveryPartners: build.query({
      query: ({ orgId, crmPartnerTypeId, includeSourceOrg }) => {
        const params = new URLSearchParams();

        if (crmPartnerTypeId)
          params.append('crmPartnerTypeId', String(crmPartnerTypeId));
        if (includeSourceOrg)
          params.append('includeSourceOrg', String(includeSourceOrg));

        const queryString = params.toString();
        return `/public/v2/organization/${orgId}/recovery/partners${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (response: any) => response?.organizationPartners,
      keepUnusedDataFor: longLife,
    }),
    getUnits: build.query({
      query: (organizationId: string | number) =>
        `/public/units/${organizationId}`,
      transformResponse: (response: any) => response?.units,
      providesTags: ['Organizations'],
    }),
    getOrgNotes: build.query({
      query: (organizationId: string | number) =>
        `/public/orgNotes/${organizationId}`,
      transformResponse: (response: any) => response?.notes,
      providesTags: ['Organizations'],
    }),
    getTissueRecovery: build.query<TissueRecoveryResp, any>({
      query: (caseId) => `/public/recoveries/${caseId}/tissues`,
      transformResponse: (response: any) => response?.record,
      providesTags: ['Recovery'],
      keepUnusedDataFor: shortLife,
    }),
    upsertTissueRecovery: build.mutation<any, TissueRecoveryForm>({
      query: (body) => ({
        url: `/public/recoveries/${body.caseId}/tissues`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Recovery'],
    }),
    getRecoverySupplyCatalog: build.query<
      CatalogResp,
      string | Date | void | null
    >({
      query: (expirationDate) => ({
        url: `/public/recoveries/supplies/catalog`,
        params: { expirationDate },
      }),
      providesTags: ['Recovery'],
      keepUnusedDataFor: shortLife,
    }),
    getRecoverySupplies: build.query<
      RecoverySuppliesResp,
      { caseId: string; tissueSupplies?: boolean }
    >({
      query: ({ caseId, tissueSupplies }) =>
        `/public/recoveries/${caseId}/supplies?tissueSupplies=${tissueSupplies}`,
      providesTags: ['Recovery'],
      keepUnusedDataFor: shortLife,
    }),
    updateRecoverySupplies: build.mutation<
      RecoverySuppliesResp,
      RecoverySuppliesResp
    >({
      query: ({ caseId, ...body }) => ({
        url: `/public/recoveries/${caseId}/supplies`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Recovery'],
    }),
    getSerologiesAll: build.query<SerologyAllApi, string>({
      query: (caseId) => `/public/serology/${caseId}/all`,
      providesTags: ['Serology'],
      keepUnusedDataFor: shortLife,
    }),
    getSerologiesAllReports: build.query<
      { serologies: SerologyReportApi[] },
      string
    >({
      query: (caseId) => `/public/serology/${caseId}/allReports`,
      providesTags: ['Serology'],
      keepUnusedDataFor: shortLife,
    }),
    addSerology: build.mutation({
      query: ({ caseId, ...body }) => ({
        url: `/public/serology/${caseId}`,
        method: 'POST',
        body: body,
      }),
      transformResponse: (response: any) => response?.serology,
      invalidatesTags: ['Serology'],
    }),
    updateSerology: build.mutation({
      query: ({ caseId, serologyId, ...body }) => ({
        url: `/public/serology/${caseId}/${serologyId}`,
        method: 'PUT',
        body: body,
      }),
      transformResponse: (response: any) => response?.serology,
      invalidatesTags: ['Serology'],
    }),
    createAuthForm: build.mutation({
      query: ({ caseId, ...body }) => ({
        url: `public/authForm/${caseId}`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['AuthForms'],
    }),
    updateAuthForm: build.mutation({
      query: ({ caseId, authFormId, ...body }) => ({
        url: `public/authForm/${caseId}/${authFormId}`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: ['AuthForms'],
    }),
    getBlankAuthForm: build.query({
      query: ({ caseId, type }) => ({
        url: `public/authForm/${caseId}/blank/${type}`,
        method: 'GET',
      }),
    }),
    getAuthForm: build.query({
      query: ({ caseId, authFormId }) => ({
        url: `public/authForm/${caseId}/${authFormId}`,
        method: 'GET',
      }),
    }),
    getAuthFormList: build.query({
      query: (caseId) => `/public/authForm/${caseId}`,
      providesTags: ['AuthForms'],
      keepUnusedDataFor: shortLife,
    }),
    deleteAuthForm: build.mutation({
      query: ({ caseId, authFormId }) => ({
        url: `/public/authForm/${caseId}/${authFormId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AuthForms'],
    }),
  }),
});

export const rtkQueryErrorLogger: Middleware =
  (api: MiddlewareAPI) => (next) => (action) => {
    if (isRejectedWithValue(action)) {
      if (import.meta.env.REACT_APP_ENVIRONMENT === 'local') {
        // eslint-disable-next-line
        console.log('API ERROR:', action);
      }
      const sentryError = Sentry.captureException(action);
      if (action.payload.data) {
        action.payload.data.type = 'error';
      }
      api.dispatch(setCurrentFeedback(action));

      if (action?.payload?.status >= 500) {
        const account = api.getState().account;
        Sentry.showReportDialog({
          subtitle:
            'Tell us what happened below without including any Protected Health Information.',
          subtitle2: '',
          labelSubmit: 'Submit Report',
          eventId: sentryError,
          user: account?.user?.firstName
            ? {
                name: `${account.user.firstName} ${account.user.lastName}`,
                email: account.user.email,
              }
            : undefined,
        });
      }
    }

    return next(action);
  };

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLogTransmitDownloadMutation,
  useGetTransmitLogsQuery,
  useGetNoteTemplatesQuery,
  useGetNoteTemplateQuery,
  useUpdateNoteTemplateMutation,
  useCreateNoteTemplateMutation,
  useGetTransmitTemplatesQuery,
  useCreateTransmitTemplateMutation,
  useUpdateTransmitTemplateMutation,
  useGetTransmitOrganizationsQuery,
  useLazyGetTransmitOrganizationsQuery,
  useGetTransmitOrganizationContactsQuery,
  useLazyGetTransmitOrganizationContactsQuery,
  useLazyGetAuthFormQuery,
  useDeleteAuthFormMutation,
  useCreateAuthFormMutation,
  useUpdateAuthFormMutation,
  useLazyGetBlankAuthFormQuery,
  useGetAuthFormListQuery,
  useAddSerologyMutation,
  useUpdateSerologyMutation,
  useGetSerologiesAllReportsQuery,
  useGetSerologiesAllQuery,
  useGetDonationDiscussionQuery,
  useUpdateDonationDiscussionMutation,
  useGetAuthorizedUsageQuery,
  useUpdateAuthorizedUsageMutation,
  useGetHemodilutionInformationQuery,
  useLazyGetHemodilutionInformationQuery,
  useUpdateHemodilutionInformationMutation,
  useLazyGetHemodilutionSerologiesQuery,
  useGetPartnersV2Query,
  useGetOrganizationPartnersV2Query,
  useGetLookupQuery,
  useGetDashboardCasesQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetOrganizationQuery,
  useGetExternalTablesQuery,
  useGetAllOrganizationsQuery,
  useGetAllOrgsContactsQuery,
  useGetCaseBannerInfoQuery,
  useGetCaseSummaryQuery,
  useLazyGetCaseSummaryQuery,
  useUpdateCaseSummaryMutation,
  useGetDispatchQuery,
  useUpsertDispatchMutation,
  useDeleteDispatchMutation,
  useGetDonorScreeningQuery,
  useLazyGetDonorScreeningQuery,
  useAddDonorScreeningMutation,
  useUpdateDonorScreeningMutation,
  useLazyGetReferralWorksheetQuery,
  useAddReferralWorksheetMutation,
  useUpdateReferralWorksheetMutation,
  useGetUnitsQuery,
  useGetOrgNotesQuery,
  useGetUsersQuery,
  useGetUserEmailsQuery,
  useGetUsersByRoleQuery,
  useGetUsersByMultipleRolesQuery,
  useGetCaseAssignmentUsersQuery,
  useGetCaseAssignmentGroupsQuery,
  useGetOrganizationProtocolQuery,
  useUploadOrganizationProtocolMutation,
  useSoftDeleteOrganizationProtocolMutation,
  useGetOrganizationsV2Query,
  useGetOutcomesQuery,
  useGetOutcomesAllQuery,
  useGetReferralWorksheetQuery,
  useGetOutcomesDetailsQuery,
  useLazyGetLookupsQuery,
  useUpdateLookupsMutation,
  useUpdateCaseTaskMutation,
  useAddCaseTaskMutation,
  useGetAllTasksOnCaseQuery,
  useGetAllTasksQuery,
  useGetTaskAuxillaryDataQuery,
  useGetTaskAllCaseIdsQuery,
  useGetCaseNotesQuery,
  useAddCaseNoteMutation,
  useGetChecklistQuery,
  useAddChecklistMutation,
  useUpdateChecklistMutation,
  useGetTissueRecoveryQuery,
  useUpsertTissueRecoveryMutation,
  useGetRecoverySupplyCatalogQuery,
  useGetRecoverySuppliesQuery,
  useUpdateRecoverySuppliesMutation,
  useLazyGetOrganizationRecoveryPartnersQuery,
} = serverState;
